import React, { useEffect, useState } from 'react'
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material'
import GenericModal from '../../../components/GenericModal'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../components/Notifications'
import { useSandboxReportsView } from '../../../hooks/useSandboxReportsView'
import { grey } from '../../../colors'

export interface SandboxReportsViewModalProps {
  isOpen: boolean
  handleCloseModal: () => void
}

/**
 * The switch behind the *Sandbox Reports* row in the reports ⋯ menu (CR-341 full cycle).
 *
 * Same shape as Sync Course Sections and Sandbox Mode: the row reports On or Off at a glance, and
 * the change is confirmed in here rather than on the click — because it changes which world the
 * reports pages are describing, and the reader should be told that before it happens rather than
 * discover it in a list that has silently become a different list.
 *
 * Nothing is written to a server. The preference is per browser while the backend decides where it
 * lives, so Save applies it and closes; there is no request to fail.
 */
const SandboxReportsViewModal: React.FC<SandboxReportsViewModalProps> = ({ isOpen, handleCloseModal }) => {
  const { showSandboxOnly, setShowSandboxOnly } = useSandboxReportsView()
  const [enabled, setEnabled] = useState(showSandboxOnly)

  // Seeded on each opening, so a cancelled change does not leave the switch on last time's
  // position. Safe to seed on the value itself here, unlike its server-backed siblings: there is
  // no read in flight to race, and nothing else moves it while this modal is open.
  useEffect(() => {
    if (isOpen) setEnabled(showSandboxOnly)
  }, [isOpen, showSandboxOnly])

  const handleSave = (): void => {
    setShowSandboxOnly(enabled)
    // No "ordinary" anywhere: it is a word that only means something to a reader who already
    // knows sandbox exists, and it was doing the work the sentence should do. Going back is the
    // default world, so it needs no adjective at all.
    notification(
      enabled ? 'Reports now show sandbox Survey Projects only' : 'Reports show your Survey Projects again',
      {
        variant: 'success',
        subTitle: enabled
          ? 'Your other Survey Projects are out of the picker until you turn this off. This changes nothing for anyone else.'
          : 'Sandbox Survey Projects are no longer offered in the picker.'
      }
    )
    handleCloseModal()
  }

  return (
    <GenericModal
      isOpen={isOpen}
      title="Sandbox Reports"
      onClose={handleCloseModal}
      maxWidth="sm"
      action={false}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(_event, checked) => { setEnabled(checked) }}
            />
          }
          label="Show sandbox Survey Projects only"
        />

        <Alert severity="info">
          <Typography variant="body2" mb={1}>
            Reports are read one Survey Project at a time, so this changes which projects the
            picker offers you — not the figures inside a report you have already opened.
          </Typography>
          <Typography variant="body2" mb={1}>
            While it is on, the picker lists <strong>only</strong> sandboxed Survey Projects, so
            the results of a rehearsal are never sitting beside the results of a real survey. Turn
            it off and it lists your Survey Projects again.
          </Typography>
          <Typography variant="body2" color={grey[700]}>
            This is yours alone — it changes nothing for anyone else, and it is remembered on this
            browser.
          </Typography>
        </Alert>

        <DefaultModalActions
          confirmButtonProps={{
            text: 'Save',
            onClick: handleSave
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
        />
      </Box>
    </GenericModal>
  )
}

export default SandboxReportsViewModal
